import { useEffect, useMemo, useRef, useState } from "react";
import { browserStorage, storageEntries } from "@/shared/browser-storage";
import type { AreaPopulationThresholds } from "@situate/contracts/runtime";
import { DEFAULT_ANALYTICS_FILTERS } from "@/features/deployment-dashboard/shared/analytics-filters";
import { useAreas } from "@/features/deployment-dashboard/shared/hooks/use-areas";
import { useThroughputLineConfigs } from "@/features/deployment-dashboard/shared/hooks/use-throughput-line-configs";
import { ALL_AREAS_FILTER_VALUE } from "@/features/deployment-dashboard/types";
import type {
  AnalyticsFilters,
  AreaLocation,
  DeploymentMonitoringScope,
} from "@/features/deployment-dashboard/types";
import { useDensityTrend } from "@/features/deployment-dashboard/widgets/charts/density-trend";
import { useQueueMetrics } from "@/features/deployment-dashboard/analytics/hooks/use-queue-metrics";
import type { DensityTrendPoint } from "@/features/deployment-dashboard/widgets/charts/density-trend";
import { usePopulationTrendAnalytics } from "@/features/deployment-dashboard/widgets/charts/population-trend-analytics";
import type { PopulationTrendAnalyticsPoint } from "@/features/deployment-dashboard/widgets/charts/population-trend-analytics";
import {
  useThroughputTrend,
  getThroughputLineOptionKey,
  type ThroughputLineTrendData,
  type ThroughputLineOption,
} from "@/features/deployment-dashboard/widgets/charts/throughput-trend";

interface AnalyticsChartState<T> {
  data: T[];
  loading: boolean;
  error: Error | null;
  thresholds?: AreaPopulationThresholds | null;
}

/**
 * Stable fallback so areas keeps the same reference across renders while unloaded, instead
 * of a fresh [] literal on every render (which would otherwise destabilize every value
 * derived from areas and any effect depending on it).
 */
const EMPTY_AREAS: AreaLocation[] = [];
const EMPTY_THROUGHPUT_LINES: ThroughputLineOption[] = [];

// TODO: This is a temporary solution for excluding internal-only throughput lines from the
// default selection. We need to discuss how to properly define "internal" lines (e.g. a
// dedicated flag on the line configuration) instead of hardcoding known line IDs here.
const DEFAULT_DESELECTED_LINE_IDS: readonly string[] = ["duty-free-entrance"];

interface AnalyticsState {
  filters: AnalyticsFilters;
  areas: AreaLocation[];
  onFiltersChange: (filters: AnalyticsFilters) => void;
  onReset: () => void;
  populationTrend: AnalyticsChartState<PopulationTrendAnalyticsPoint>;
  dutyFreePopulationTrend: AnalyticsChartState<PopulationTrendAnalyticsPoint>;
  densityTrend: AnalyticsChartState<DensityTrendPoint>;
  queueMetrics: ReturnType<typeof useQueueMetrics>;
  throughputLineTrendData: ThroughputLineTrendData[];
  throughputLineTrendLoading: boolean;
  throughputLineTrendError: Error | null;
  throughputAllLineTrendData: ThroughputLineTrendData[];
  throughputLines: ThroughputLineOption[];
  throughputLinesLoading: boolean;
  throughputLinesError: Error | null;
  selectedThroughputLineKeys: ReadonlySet<string>;
  selectedThroughputLines: ThroughputLineOption[];
  onThroughputLinesChange: (keys: ReadonlySet<string>) => void;
}

/**
 * Owns the Analytics page's local filter state (start date, end date, area) and assembles the
 * data for its charts. Areas come from the same runtime source as Live View.
 *
 * Population Trend and Density Trend charts refetch from the runtime API whenever the date
 * range or area filter changes. "All Areas" sends every available area ID to the runtime API.
 *
 * Throughput Trend fetches data for every configured line up front (so the side panel can show
 * each line's In/Out/Net totals as soon as the page loads), and derives the selected subset used
 * to render an aggregated chart and drive its loading/error state. Changing the area filter resets
 * line selections to that area's defaults. Duty Free Entrance is excluded from the All Areas
 * default only (see TODO below); within a specific area, all available lines are selected.
 */
export function useAnalytics({ deploymentId }: DeploymentMonitoringScope): AnalyticsState {
  const [filterState, setFilterState] = useState({
    deploymentId,
    filters: DEFAULT_ANALYTICS_FILTERS,
    areaRestored: false,
  });
  const filters =
    filterState.deploymentId === deploymentId ? filterState.filters : DEFAULT_ANALYTICS_FILTERS;
  const areaRestored = filterState.deploymentId === deploymentId && filterState.areaRestored;
  const { data: areas = EMPTY_AREAS, isSuccess: areasLoaded } = useAreas(deploymentId);

  useEffect(() => {
    if (!areasLoaded || areaRestored) return;

    const savedAreaId = browserStorage.read(storageEntries.preferredArea(deploymentId));
    const areaId =
      savedAreaId !== undefined &&
      (savedAreaId === ALL_AREAS_FILTER_VALUE || areas.some((area) => area.id === savedAreaId))
        ? savedAreaId
        : DEFAULT_ANALYTICS_FILTERS.areaId;
    setFilterState({
      deploymentId,
      filters: { ...filters, areaId },
      areaRestored: true,
    });
  }, [areasLoaded, areas, areaRestored, deploymentId, filters]);

  const selectedAreaIds = useMemo(
    () =>
      !areaRestored
        ? []
        : areas
            .filter(
              (area) => filters.areaId === ALL_AREAS_FILTER_VALUE || area.id === filters.areaId
            )
            .map((area) => area.id),
    [areas, filters.areaId, areaRestored]
  );

  const throughputAreas = useMemo(
    () =>
      !areaRestored
        ? EMPTY_AREAS
        : filters.areaId === ALL_AREAS_FILTER_VALUE
          ? areas
          : areas.filter((area) => area.id === filters.areaId),
    [filters.areaId, areas, areaRestored]
  );
  const throughputLineConfigsQuery = useThroughputLineConfigs(deploymentId, throughputAreas);
  const throughputLines = throughputLineConfigsQuery.lines;
  const areThroughputLineConfigsLoading = throughputLineConfigsQuery.isLoading;
  const [selectedThroughputLineKeys, setSelectedThroughputLineKeys] = useState<ReadonlySet<string>>(
    new Set()
  );
  const appliedThroughputScope = useRef<{ deploymentId: string; areaId: string } | null>(null);

  // Content-derived signatures (not the throughputAreas/throughputLines array references)
  // that the effect below keys off of, so it only re-runs when the actual area/line selection
  // changes. Depending on the arrays directly previously caused an infinite render loop, since
  // some of their upstream sources produced a new array identity on every render even when
  // their contents were unchanged.
  const throughputAreaIdsKey = throughputAreas.map((area) => area.id).join(",");
  const throughputLineKeysKey = throughputLines.map(getThroughputLineOptionKey).join(",");

  useEffect(() => {
    if (areThroughputLineConfigsLoading) return;

    const availableKeys = throughputLines.map(getThroughputLineOptionKey);
    const availableKeysSet = new Set(availableKeys);
    const defaultSelectedKeys = throughputLines
      .filter(
        (line) =>
          filters.areaId !== ALL_AREAS_FILTER_VALUE ||
          !DEFAULT_DESELECTED_LINE_IDS.includes(line.line_id)
      )
      .map(getThroughputLineOptionKey);
    const scopeChanged =
      appliedThroughputScope.current === null ||
      appliedThroughputScope.current.deploymentId !== deploymentId ||
      appliedThroughputScope.current.areaId !== filters.areaId;
    appliedThroughputScope.current = { deploymentId, areaId: filters.areaId };

    setSelectedThroughputLineKeys((current) => {
      const preserved = [...current].filter((key) => availableKeysSet.has(key));
      const nextKeys = scopeChanged ? defaultSelectedKeys : preserved;

      return nextKeys.length === current.size && nextKeys.every((key) => current.has(key))
        ? current
        : new Set(nextKeys);
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    areThroughputLineConfigsLoading,
    deploymentId,
    filters.areaId,
    throughputAreaIdsKey,
    throughputLineKeysKey,
  ]);

  const selectedThroughputLines = useMemo(
    () =>
      throughputLines.filter((line) =>
        selectedThroughputLineKeys.has(getThroughputLineOptionKey(line))
      ),
    [throughputLines, selectedThroughputLineKeys]
  );

  const populationTrend = usePopulationTrendAnalytics({
    deploymentId,
    areaIds: selectedAreaIds,
    startDate: filters.startDate,
    endDate: filters.endDate,
    derivedPopulationMetrics:
      filters.areaId === ALL_AREAS_FILTER_VALUE
        ? null
        : areas.find((area) => area.id === filters.areaId)?.derivedPopulationMetrics,
  });
  const densityTrend = useDensityTrend({
    deploymentId,
    areaIds: selectedAreaIds,
    startDate: filters.startDate,
    endDate: filters.endDate,
  });
  const queueMetrics = useQueueMetrics(
    deploymentId,
    selectedAreaIds,
    filters.startDate,
    filters.endDate
  );
  const throughputTrendState = useThroughputTrend({
    deploymentId,
    selectedLines: areThroughputLineConfigsLoading ? EMPTY_THROUGHPUT_LINES : throughputLines,
    startDate: filters.startDate,
    endDate: filters.endDate,
  });

  const selectedThroughputLineTrendData = useMemo(
    () =>
      throughputTrendState.lines.filter((data) =>
        selectedThroughputLineKeys.has(getThroughputLineOptionKey(data.line))
      ),
    [throughputTrendState.lines, selectedThroughputLineKeys]
  );

  function handleFiltersChange(nextFilters: AnalyticsFilters) {
    if (nextFilters.areaId !== filters.areaId) {
      browserStorage.write(storageEntries.preferredArea(deploymentId), nextFilters.areaId);
    }
    setFilterState({
      deploymentId,
      filters: nextFilters,
      areaRestored: areaRestored || nextFilters.areaId !== filters.areaId,
    });
  }

  function handleReset() {
    setFilterState({
      deploymentId,
      filters: { ...DEFAULT_ANALYTICS_FILTERS, areaId: filters.areaId },
      areaRestored,
    });
    setSelectedThroughputLineKeys(new Set());
  }

  return {
    filters,
    areas,
    onFiltersChange: handleFiltersChange,
    onReset: handleReset,
    populationTrend,
    dutyFreePopulationTrend: {
      data: populationTrend.dutyFreeData ?? [],
      loading: populationTrend.loading,
      error: populationTrend.error,
    },
    densityTrend,
    queueMetrics,
    throughputLineTrendData: selectedThroughputLineTrendData,
    throughputLineTrendLoading: selectedThroughputLineTrendData.some((data) => data.loading),
    throughputLineTrendError:
      selectedThroughputLineTrendData.find((data) => data.error != null)?.error ?? null,
    throughputAllLineTrendData: throughputTrendState.lines,
    throughputLines,
    throughputLinesLoading: throughputLineConfigsQuery.isLoading,
    throughputLinesError: throughputLineConfigsQuery.error,
    selectedThroughputLineKeys,
    selectedThroughputLines,
    onThroughputLinesChange: setSelectedThroughputLineKeys,
  };
}
